import { Avatar, Box, CardContent, Chip, Grid, Pagination, Stack, useTheme } from "@mui/material";
import { MprcButton, MprcCardActions, MprcConfirmDialog, MprcFormSubHeader, MprcIconButton, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { IconAnglesRight, IconDelete } from "components/icons";
import { MprcContainer } from "components/MprcContainer";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { ManagedUserDtoPaginatedList, ManagedUserListOrderByQuery, ManagedUsersPayload } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IAdvFilterParams } from "views/general/AdvancedFilter";
import { Filter } from "./UserAdvSelectorFilter";
////////////////////////////////////////////////////////////////////////////////

const AllowSelectMaxItems = 200;
const DefaultPageSize = 10;

export type UserAdvSelectorResult = {
    userId: string;
    name: string;
    thumbnailUrl: string;
};

export interface IUserFilterOptions extends IAdvFilterParams<ManagedUserListOrderByQuery> { }

interface IViewDataFilter {
    pageNumber: number;
    filter: IUserFilterOptions;
}

interface IPros {
    buttonText?: string;
    onFinish: (users: UserAdvSelectorResult[] | undefined) => void;
}

////////////////////////////////////////////////////////////////////////////////

export const UserAdvSelector: React.FC<IPros> = (props) => {
    ////////////////////////////////////////////////////////////////////////////////
    const api = useCoreApi();
    const { t } = useTranslation();
    const { onFinish, buttonText } = props;
    const theme = useTheme();

    const [viewError, setViewError] = useState<any>(null);
    const [viewFilter, setViewFilter] = useState<IViewDataFilter>({
        pageNumber: 1,
        filter: {
            searchText: "",
            advFilters: [],
            order: ManagedUserListOrderByQuery.Name
        }
    });
    const [viewData, setViewData] = useState<ManagedUserDtoPaginatedList | null>(null);
    const [dialog, setDialog] = useState<any>(undefined);
    const [selection, setSelection] = useState<UserAdvSelectorResult[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectAll, setSelectAll] = useState<boolean>(false);

    const { enqueueSnackbar } = useSnackbar();

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const buildFilter = (forceLimit?: number | null): ManagedUsersPayload => {
        const { pageNumber, filter } = viewFilter;
        return {
            searchText: filter.searchText ?? null,
            filters: filter.advFilters,
            isSelectable: true,
            forceLimit: forceLimit ?? null,
            orderBy: filter.order,
            pageNumber,
            pageSize: DefaultPageSize,
        };
    };

    ///
    const handleFilterChange = useCallback((filter: IUserFilterOptions) => {
        setViewFilter({ pageNumber: 1, filter });
        setLoading(true);
    }, []);

    ///
    const handleSelection = (user: UserAdvSelectorResult) => {
        if (selection.length >= AllowSelectMaxItems)
            enqueueSnackbar(t("User.UserAdvSelector.AllowSelectMaxItemsMessage", { count: AllowSelectMaxItems }), { variant: "error" });
        else if (selection.includes(user))
            enqueueSnackbar(t("User.UserAdvSelector.AlreadySelectedMessage", { name: user.name }), { variant: "error" });
        else
            setSelection([...selection, user]);
    };

    ///
    const handleFinishClick = () => {
        if (selection.length == 0)
            enqueueSnackbar(t("User.UserAdvSelector.NotSelectionMessage"), { variant: "error" });
        else
            onFinish(selection);
    };

    ///
    const RenderFilter = (
        <MprcContainer>
            <Filter
                filterOptions={viewFilter.filter}
                onFilterChange={handleFilterChange}
            />
        </MprcContainer>
    );

    ///
    const RenderListUser = (
        <>
            <MprcContainer>

                {viewData && viewData.totalRows > 0 && (
                    <>
                        <CardContent>
                            <Grid container justifyContent="center" alignItems="center" spacing={2} maxWidth="100%">
                                {viewData.items.map((item) => {
                                    return (
                                        <Grid size={{ xs: 12 }} key={item.userId} width="100%">
                                            <Grid container justifyContent="center" alignItems="center" spacing={1} wrap="nowrap">
                                                <Grid>
                                                    <Avatar
                                                        src={item.thumbnailUrl}
                                                        sx={(theme) => ({ width: 32, height: 32, backgroundColor: theme.palette.grey[200] })}
                                                        onClick={() => handleSelection(item)}
                                                    />
                                                </Grid>
                                                <Grid size="grow" overflow="hidden">
                                                    <Grid container alignItems="center" spacing={1} flexWrap="nowrap" width="100%">
                                                        <Grid overflow="hidden">
                                                            <MprcListItemTitle onClick={() => handleSelection(item)}>
                                                                {item.name}
                                                            </MprcListItemTitle>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </CardContent>
                        <MprcCardActions>
                            {viewData && viewData.totalPages > 1 && (
                                <Pagination
                                    shape="rounded"
                                    color="primary"
                                    page={viewData.pageNumber}
                                    count={viewData.totalPages}
                                    onChange={(e, pageNumber) => {
                                        if (pageNumber != viewFilter.pageNumber) {
                                            setViewFilter({
                                                ...viewFilter,
                                                pageNumber
                                            });
                                            setLoading(true);
                                        }
                                    }}
                                />
                            )}
                        </MprcCardActions>
                    </>
                )}
            </MprcContainer>
        </>
    );

    const RenderSelectedUsers = (
        <MprcContainer>
            <Stack direction="row" justifyContent="flex-start" alignContent="flex-start" flexWrap="wrap">
                {
                    selection?.map((item, index) =>
                        <Chip
                            key={index}
                            label={item.name}
                            size="small"
                            sx={{ mb: 0.5, mr: 0.5 }}
                            avatar={<Avatar src={item.thumbnailUrl} />}
                            onDelete={() => setSelection(selection.filter(p => p.userId != item.userId))}
                        />
                    )
                }
            </Stack>
        </MprcContainer>
    );

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    // Ao carregar
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.userManagement.managedUsers(buildFilter());
                setViewData(result.data);
            } catch (e) {
                setViewData(null);
                setViewError(e);
            }

            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);


    // Ao selecionar todos
    useEffect(() => {
        async function getData() {
            try {

                if (selection.length >= AllowSelectMaxItems) {
                    enqueueSnackbar(t("User.UserAdvSelector.AllowSelectMaxItemsMessage", { count: AllowSelectMaxItems }), { variant: "error" });
                    setSelectAll(false);
                    return;
                }

                var result = await api.v1.userManagement.managedUsers(buildFilter(AllowSelectMaxItems * 2));

                var addUsers = result.data.items.filter((item) => selection.findIndex(x => x.userId == item.userId) == -1);

                setSelectAll(false);

                if (selection.length + addUsers.length > AllowSelectMaxItems) {
                    addUsers = addUsers.slice(0, AllowSelectMaxItems - selection.length);

                    setDialog(
                        <MprcConfirmDialog
                            open
                            severity="danger"
                            message={<span>{t("User.UserAdvSelector.ConfirmSelectedAllMessage", { limit: AllowSelectMaxItems, count: addUsers.length })}</span>}
                            onClose={
                                (confirm) => {
                                    if (confirm && addUsers) {
                                        setSelection([...selection, ...addUsers as UserAdvSelectorResult[]]);
                                    }
                                    setDialog(null);
                                }
                            }
                        />
                    );
                }
                else {
                    setSelection([...selection, ...addUsers as UserAdvSelectorResult[]]);
                }

            } catch (e) {
                setViewData(null);
                setViewError(e);
            }
        }

        if (selectAll) getData();
    }, [selectAll]);


    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestErrorReport error={viewError} />

            <MprcRequestIndicator visible={loading || selectAll} />

            {dialog}

            {RenderFilter}

            <Box display="flex" flexDirection="row" height={viewFilter.filter.advFilters.length == 0 ? 405 : 354} width="100%">
                <Box display="flex" flexDirection="column" flexBasis="100%" flex={1} maxWidth="50%">
                    <Box>
                        <Grid container justifyContent="end" alignItems="center" spacing={2}>
                            <Grid size="grow">
                                <MprcFormSubHeader
                                    title={t("User.UserAdvSelector.ListUserSubHeaderTitle", { count: viewData?.totalRows })}
                                    subtitle={t("User.UserAdvSelector.ListUserSubHeaderSubtitle")}
                                />
                            </Grid>
                            <Grid mr={1}>
                                <MprcIconButton
                                    size="small"
                                    onClick={() => setSelectAll(true)}
                                >
                                    <IconAnglesRight color="muted" />
                                </MprcIconButton>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box mt={1} overflow="auto">
                        {RenderListUser}
                    </Box>
                </Box>
                <Box display="flex" flexDirection="column" width={10} style={{ height: "100%", backgroundColor: theme.palette["chip"].main, borderStyle: "none" }} />
                <Box display="flex" flexDirection="column" flexBasis="100%" flex={1} maxWidth="49%">
                    <Box ml={1}>
                        <Grid container justifyContent="end" alignItems="center" spacing={2}>
                            <Grid size="grow">
                                <MprcFormSubHeader
                                    title={t("User.UserAdvSelector.SelectionUsersSubHeaderTitle", { count: selection.length })}
                                    subtitle={t("User.UserAdvSelector.SelectionUsersSubHeaderSubtitle", { count: AllowSelectMaxItems })}
                                />
                            </Grid>
                            <Grid>
                                <MprcIconButton
                                    size="small"
                                    disabled={selection.length == 0}
                                    onClick={() => setSelection([])}
                                >
                                    <IconDelete color="muted" />
                                </MprcIconButton>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box ml={1} mt={1} overflow="auto">
                        {RenderSelectedUsers}
                    </Box>
                </Box>
            </Box>

            <Grid container mt={1}>
                <Grid width={"100%"} container alignItems="center">
                    <Grid size="grow">
                        <Grid container justifyContent="end" alignItems="center" spacing={2}>
                            <Grid>
                                <MprcButton
                                    title={buttonText ? buttonText : t("User.UserAdvSelector.FinishButtonText")}
                                    text={buttonText ? buttonText : t("User.UserAdvSelector.FinishButtonText")}
                                    color="info"
                                    variant="contained"
                                    onClick={handleFinishClick}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
